<script lang="ts" setup>
import { h, computed } from "vue";
import { NDropdown, NTag, NPopover } from "naive-ui";
import type { Comment } from "../models/contador";
import { formatJSDate } from "../utils/dates";
import RichTextEditor from "./RichTextEditor.vue";
import { useOrganization } from "../hooks/useOrganization";
import { getUserEmail } from "../config/firebase.ts";

const props = withDefaults(
    defineProps<{
        comment: Comment;
        isEditing: boolean;
        editingContent?: string;
        originalContent?: string;
        hasDraft?: boolean;
        isUpdating?: boolean;
    }>(),
    {
        editingContent: "",
        originalContent: "",
        hasDraft: false,
        isUpdating: false,
    },
);

const emit = defineEmits<{
    (e: "edit"): void;
    (e: "delete"): void;
    (e: "pin"): void;
    (e: "archive"): void;
    (e: "update:editing-content", value: string): void;
    (e: "save"): void;
    (e: "cancel"): void;
    (e: "delete-draft"): void;
    (e: "set-editor-ref", el: Element | null): void;
}>();

function getCommentDropdownOptions(comment: Comment) {
    const isArchived = Boolean(comment.archived);
    const isPinned = Boolean(comment.pinned);
    const currentEmail = getUserEmail()?.toLowerCase() ?? null;
    const isOwner = !!currentEmail && comment.author?.toLowerCase() === currentEmail;
    return [
        { label: "Edit", key: "edit", disabled: isArchived || !isOwner },
        { label: isPinned ? "Unpin" : "Pin", key: "pin-toggle", disabled: isArchived },
        {
            label: isArchived ? "Unarchive" : "Archive",
            key: "archive-toggle",
        },
        { type: "divider", key: "divider" },
        {
            key: "delete",
            label: () => h("span", { style: { color: "#e53e3e" } }, "Delete"),
            disabled: !isOwner,
        },
    ];
}

function handleDropdownSelect(key: string | number) {
    if (key === "delete") {
        emit("delete");
    } else if (key === "edit") {
        emit("edit");
    } else if (key === "pin-toggle") {
        emit("pin");
    } else if (key === "archive-toggle") {
        emit("archive");
    }
}

function handleEditorRef(ref: any, _refs?: Record<string, any>) {
    emit("set-editor-ref", (ref as Element) ?? null);
}

function isTeamAuthor(email: string | undefined | null): boolean {
    if (!email) return false;
    return /@proper\.ai$/i.test(email.trim());
}

const { contactEmailToName } = useOrganization();

const displayName = computed(() => {
    const email = props.comment.author;
    const orgName = contactEmailToName(email);
    const currentEmail = getUserEmail()?.toLowerCase() ?? null;
    const isOwner = !!currentEmail && email?.toLowerCase() === currentEmail;
    let name = orgName || email || "Unknown";
    if (isOwner) {
        name += " (you)";
    }
    return name;
});
</script>

<template>
    <li :class="['mb-4 p-3 border border-gray-200 rounded-lg', comment.pinned ? 'bg-gray-50' : '']">
        <div class="flex items-center justify-between gap-2">
            <div class="w-full flex flex-wrap justify-between">
                <div class="flex items-center gap-1">
                    <span
                        v-if="comment.pinned"
                        class="material-symbols-outlined text-amber-600 text-lg w-fit"
                        title="Pinned"
                        aria-label="Pinned"
                    >
                        push_pin
                    </span>
                    <n-tag :type="isTeamAuthor(comment.author) ? 'success' : 'info'" class="w-fit">
                        {{ isTeamAuthor(comment.author) ? "Team" : "Customer" }}
                    </n-tag>
                    <n-tag v-if="comment.archived" type="default" class="w-fit"> Archived </n-tag>
                    <n-popover trigger="hover" class="w-fit">
                        <template #trigger>
                            <span class="font-semibold cursor-default">{{ displayName }}</span>
                        </template>
                        {{ comment.author }}
                    </n-popover>
                </div>

                <time class="flex items-center w-fit lowercase text-gray-400">
                    {{
                        formatJSDate(new Date(comment.createdAt), {
                            include_time: true,
                            include_year: true,
                        }).RELATIVE_DATE_FORMAT
                    }}
                </time>
            </div>
            <n-dropdown
                :options="getCommentDropdownOptions(comment)"
                trigger="click"
                @select="handleDropdownSelect"
            >
                <button
                    class="bg-white rounded-lg border border-gray-200 w-5 flex items-center justify-center"
                >
                    <span class="material-symbols-outlined text-sm"> more_vert</span>
                </button>
            </n-dropdown>
        </div>

        <!-- Edit mode -->
        <div v-if="isEditing" :ref="handleEditorRef">
            <div class="mt-2">
                <rich-text-editor
                    :model-value="editingContent"
                    @update:model-value="(val) => $emit('update:editing-content', val)"
                    :show-file-upload="false"
                    show-submit-button
                    :submit-disabled="
                        isUpdating ||
                        !editingContent.trim() ||
                        !(editingContent.trim() !== originalContent.trim() || hasDraft)
                    "
                    placeholder="Edit comment..."
                    button-text="Save changes"
                    @submit="$emit('save')"
                >
                    <button v-if="hasDraft" class="secondary-button" @click="$emit('delete-draft')">
                        Delete draft
                    </button>

                    <button class="secondary-button" @mousedown.stop @click="$emit('cancel')">
                        Cancel
                    </button>
                </rich-text-editor>
            </div>
        </div>

        <!-- Read mode -->
        <div v-else>
            <div v-if="comment?.text" class="rich-content mt-2">
                <div v-html="comment.text"></div>
            </div>
            <div v-if="hasDraft" class="mt-2 text-xs text-gray-500">
                Draft saved, click on edit to see it.
            </div>
        </div>
    </li>
</template>

<style scoped>
.rich-content :deep(a) {
  color: #2563eb; /* Tailwind blue-600 */
  text-decoration: underline;
  cursor: pointer;
}
.rich-content :deep(a:hover) {
  color: #1d4ed8; /* blue-700 */
}
.rich-content :deep(a[target="_blank"])::after {
  content: "";
}
</style>

